import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { io as socketIo } from 'socket.io-client';
import API_BASE_URL from '../config';
import './SupportWidget.css';

const SESSION_STORAGE_KEY = 'mintaka_chatbot_session_v1';
const CLOSED_SIZE = 96;
const OPEN_WIDTH = 420;
const OPEN_HEIGHT = 620;

// Derive WS base from API_BASE_URL (strip trailing slash)
const WS_BASE = API_BASE_URL.replace(/\/+$/, '').replace(/\/api$/, '');

const getInitialPosition = () => {
  if (typeof window === 'undefined') {
    return { x: 32, y: 32 };
  }
  return {
    x: Math.max(16, (window.innerWidth || 0) - OPEN_WIDTH - 24),
    y: Math.max(16, (window.innerHeight || 0) - OPEN_HEIGHT - 24),
  };
};

const formatAssistantReply = (reply, fallback) => {
  if (!fallback) return reply;
  return `${reply}\n\n(He escalado esta conversación para seguimiento humano.)`;
};

// ── TTS helper ────────────────────────────────────────────────────────────────
function speak(text) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = 'es-MX';
  utter.rate = 1.05;
  utter.pitch = 1;
  // Prefer a Spanish voice if available
  const voices = window.speechSynthesis.getVoices();
  const esVoice = voices.find((v) => v.lang.startsWith('es'));
  if (esVoice) utter.voice = esVoice;
  window.speechSynthesis.speak(utter);
}

const SupportWidget = () => {
  const token = useSelector((state) => state.auth.token);
  const decoded = useMemo(() => {
    try {
      return token ? jwtDecode(token) : null;
    } catch {
      return null;
    }
  }, [token]);

  const estacion = decoded?.estacion || null;

  // ── Chat state ──────────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [flightModalOpen, setFlightModalOpen] = useState(false);
  const [flightForm, setFlightForm] = useState({
    airline: '', flightNumber: '', origin: '', destination: '',
    date: '', departure: '', arrival: '',
  });
  const [lastFlightQuery, setLastFlightQuery] = useState(null);
  const [passengerModalData, setPassengerModalData] = useState(null);
  const [detailPromptId, setDetailPromptId] = useState(null);
  const [detailPromptInput, setDetailPromptInput] = useState('');

  // ── Voice state ─────────────────────────────────────────────────────────────
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  // ── Alert state ─────────────────────────────────────────────────────────────
  const [alerts, setAlerts] = useState([]);         // { id, type, level, title, message, ts }
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const flightPromptMessageIdRef = useRef(null);
  const seatMessageIdRef = useRef(null);
  const [position, setPosition] = useState(getInitialPosition);
  const dragState = useRef({ isDragging: false, offsetX: 0, offsetY: 0 });
  const widgetRef = useRef(null);
  const chatBodyRef = useRef(null);
  const inputRef = useRef(null);
  const socketRef = useRef(null);

  // ── Socket connection ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !estacion) return;

    const socket = socketIo(WS_BASE, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join:estacion', estacion);
      console.log('🔌 Tony socket conectado, sala:', estacion);
    });

    socket.on('tony:alert', (alert) => {
      const id = `${alert.type}-${alert.ts}`;
      setAlerts((prev) => {
        // Avoid duplicates
        if (prev.some((a) => a.id === id)) return prev;
        return [{ id, ...alert }, ...prev].slice(0, 20);
      });
      if (!isOpen) {
        setUnreadAlerts((n) => n + 1);
      }
      // Inject alert as Tony message in the chat
      setMessages((prev) => [
        ...prev,
        {
          id: `alert-${id}`,
          role: 'assistant',
          text: `🔔 **${alert.title}**\n${alert.message}`,
          createdAt: new Date().toISOString(),
          metadata: { isAlert: true, level: alert.level },
        },
      ]);
      if (ttsEnabled) {
        speak(`${alert.title}. ${alert.message}`);
      }
    });

    socket.on('disconnect', () => console.log('🔌 Tony socket desconectado'));

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, estacion]);

  // When chat opens, clear unread counter
  useEffect(() => {
    if (isOpen) setUnreadAlerts(0);
  }, [isOpen]);

  // ── Push-to-talk ────────────────────────────────────────────────────────────
  const SpeechRecognition =
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const startRecording = useCallback(() => {
    if (!SpeechRecognition || isRecording) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-MX';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputValue(transcript);
      setIsRecording(false);
    };

    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);

    recognition.start();
    setIsRecording(true);
  }, [SpeechRecognition, isRecording]);

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, []);

  // ── Session helpers ─────────────────────────────────────────────────────────
  const persistSessionId = (value) => {
    try {
      window.localStorage.setItem(SESSION_STORAGE_KEY, value);
    } catch { /* ignore */ }
  };

  const restoreSessionId = () => {
    try {
      return window.localStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  };

  const clampPosition = useCallback(
    (x, y, open = isOpen) => {
      const widget = widgetRef.current;
      const width = widget?.offsetWidth || (open ? OPEN_WIDTH : CLOSED_SIZE);
      const height = widget?.offsetHeight || (open ? OPEN_HEIGHT : CLOSED_SIZE);
      const maxX = (window.innerWidth || 0) - width - 16;
      const maxY = (window.innerHeight || 0) - height - 16;
      return {
        x: Math.max(16, Math.min(x, maxX < 16 ? 16 : maxX)),
        y: Math.max(16, Math.min(y, maxY < 16 ? 16 : maxY)),
      };
    },
    [isOpen],
  );

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const stored = restoreSessionId();
    if (stored) { setSessionId(stored); return stored; }
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/chatbot/sessions`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      const newId = response.data?.sessionId;
      if (newId) { setSessionId(newId); persistSessionId(newId); return newId; }
    } catch (err) {
      console.error('No se pudo crear sesión de chatbot:', err?.response?.status || err.message);
    }
    return null;
  }, [sessionId, token]);

  const loadConversation = useCallback(async (activeSessionId) => {
    if (!activeSessionId) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}api/chatbot/conversations/${encodeURIComponent(activeSessionId)}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      const history = Array.isArray(response.data?.messages) ? response.data.messages : [];
      setMessages(history.map((msg) => ({
        id: msg.id || `${msg.role}-${msg.createdAt}`,
        role: msg.role,
        text: msg.content,
        createdAt: msg.createdAt,
        metadata: msg.metadata || {},
      })));
    } catch (err) {
      console.error('No se pudo recuperar historial:', err?.response?.status || err.message);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  const autoScroll = () => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    ensureSession().then((id) => loadConversation(id).then(() => setTimeout(autoScroll, 150)));
  }, [ensureSession, isOpen, loadConversation]);

  useEffect(() => {
    setPosition((prev) => clampPosition(prev.x, prev.y));
  }, [clampPosition, isOpen]);

  useEffect(() => {
    const handleResize = () => setPosition((prev) => clampPosition(prev.x, prev.y));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampPosition]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => { autoScroll(); }, [messages, isTyping]);

  const openFlightForm = useCallback(
    (options = {}) => {
      const { reset = false } = options;
      setFlightForm((prev) => ({
        airline: reset ? (lastFlightQuery?.airline || '') : prev.airline || lastFlightQuery?.airline || '',
        flightNumber: reset ? (lastFlightQuery?.number || '') : prev.flightNumber || lastFlightQuery?.number || '',
        origin: reset ? '' : prev.origin || '',
        destination: reset ? '' : prev.destination || '',
        date: reset ? '' : prev.date || '',
        departure: reset ? '' : prev.departure || '',
        arrival: reset ? '' : prev.arrival || '',
      }));
      setFlightModalOpen(true);
    },
    [lastFlightQuery],
  );

  // Detect pending actions and SABRE data from last assistant message
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    if (last.role !== 'assistant') return;

    if (/¿Quieres que lo demos de alta\?/i.test(last.text)) {
      if (!pendingAction || pendingAction.messageId !== last.id) {
        setPendingAction({ type: 'registerConfirm', messageId: last.id });
      }
    } else if (pendingAction?.type === 'registerConfirm') {
      setPendingAction(null);
    }

    if (/Perfecto, ind[íi]c(?:a|ame) origen/i.test(last.text)) {
      setDetailPromptId(last.id);
      setDetailPromptInput('');
      if (flightPromptMessageIdRef.current !== last.id) {
        openFlightForm({ reset: true });
        flightPromptMessageIdRef.current = last.id;
      }
    } else if (detailPromptId) {
      setDetailPromptId(null);
      setDetailPromptInput('');
    }

    const seatMatch = last.text.match(/Servicios de silla[^0-9]*(\d+)/i);
    if (seatMatch && seatMessageIdRef.current !== last.id) {
      const infoMatch = last.text.match(/Vuelo\s+0*(\d{2,4})\s+([A-Z]{3})\s+→\s+([A-Z]{3}).*?para\s+(\d{4}-\d{2}-\d{2})/i);
      const passengers = [];
      const passengerSectionIndex = last.text.indexOf('Pasajeros SABRE');
      if (passengerSectionIndex !== -1) {
        const section = last.text.slice(passengerSectionIndex);
        const rawMatches = section.match(/\{[\s\S]*?\}/g) || [];
        for (const raw of rawMatches) {
          try {
            const normalized = raw
              .replace(/: nulo/gi, ': null')
              .replace(/: Verdadero/gi, ': true')
              .replace(/: Falso/gi, ': false');
            passengers.push(JSON.parse(normalized));
          } catch { /* ignore */ }
        }
      }
      setPassengerModalData({
        airline: lastFlightQuery?.airline || null,
        flightNumber: infoMatch?.[1] || lastFlightQuery?.number || '',
        origin: infoMatch?.[2] || '',
        destination: infoMatch?.[3] || '',
        date: infoMatch?.[4] || '',
        seatCount: passengers.length || Number(seatMatch[1]) || 0,
        passengers,
        raw: last.text,
        messageId: last.id,
      });
      seatMessageIdRef.current = last.id;
    }
  }, [detailPromptId, lastFlightQuery, messages, openFlightForm, pendingAction]);

  // ── Drag ────────────────────────────────────────────────────────────────────
  const startDrag = (event) => {
    event.preventDefault();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    dragState.current = { isDragging: true, offsetX: clientX - position.x, offsetY: clientY - position.y };
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('touchmove', onDrag);
    document.addEventListener('touchend', endDrag);
  };

  const onDrag = (event) => {
    if (!dragState.current.isDragging) return;
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    setPosition(clampPosition(clientX - dragState.current.offsetX, clientY - dragState.current.offsetY));
  };

  const endDrag = () => {
    dragState.current.isDragging = false;
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', endDrag);
    document.removeEventListener('touchmove', onDrag);
    document.removeEventListener('touchend', endDrag);
  };

  // ── Messaging ───────────────────────────────────────────────────────────────
  const appendMessage = useCallback((payload) => {
    setMessages((prev) => [...prev, payload]);
  }, []);

  const sendMessage = useCallback(
    async (text, options = {}) => {
      const { appendUser = true, skipFlightDetection = false } = options;
      const trimmed = (text || '').trim();
      if (!trimmed) return;

      let activeSessionId = sessionId;
      if (!activeSessionId) activeSessionId = await ensureSession();
      if (!activeSessionId) return;

      setSessionId(activeSessionId);
      setPendingAction(null);

      if (appendUser) {
        setPassengerModalData(null);
        if (!skipFlightDetection) {
          const flightMatch = trimmed.match(/vuelo\s+([a-z]{2,3})?\s*0*(\d{2,4})/i);
          if (flightMatch) {
            setLastFlightQuery({
              airline: flightMatch[1] ? flightMatch[1].toUpperCase() : null,
              number: flightMatch[2],
              raw: trimmed,
            });
          }
        }
        appendMessage({ id: `user-${Date.now()}`, role: 'user', text: trimmed, createdAt: new Date().toISOString() });
        setInputValue('');
      }

      setIsTyping(true);
      try {
        const response = await axios.post(
          `${API_BASE_URL}api/chatbot/message`,
          { sessionId: activeSessionId, message: trimmed },
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const reply = response?.data?.reply || 'No pude interpretar tu solicitud.';
        const formatted = formatAssistantReply(reply, response?.data?.fallback);
        const assistantMsg = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          text: formatted,
          createdAt: new Date().toISOString(),
          metadata: { fallback: Boolean(response?.data?.fallback) },
        };
        appendMessage(assistantMsg);
        if (ttsEnabled) speak(formatted);
      } catch (err) {
        console.error('Error chatbot:', err?.response?.status || err.message);
        appendMessage({
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          text: 'No pude comunicarme con Mintaka. Intenta de nuevo.',
          createdAt: new Date().toISOString(),
          metadata: { error: true },
        });
      } finally {
        setIsTyping(false);
      }
    },
    [appendMessage, ensureSession, sessionId, token, ttsEnabled],
  );

  const handleSend = () => sendMessage(inputValue);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleQuickAction = (text) => sendMessage(text, { skipFlightDetection: true });

  const handleFlightChange = (field, value) => setFlightForm((prev) => ({ ...prev, [field]: value }));
  const closeFlightModal = () => setFlightModalOpen(false);

  const handleFlightSubmit = async (event) => {
    event.preventDefault();
    const { airline, flightNumber, origin, destination, date, departure, arrival } = flightForm;
    if (!origin || !destination || !date || !departure || !arrival) {
      alert('Completa origen, destino, fecha y horarios.');
      return;
    }
    if (airline && flightNumber) {
      const airlineCode = airline.toUpperCase();
      if (!lastFlightQuery || lastFlightQuery.airline !== airlineCode || lastFlightQuery.number !== flightNumber) {
        await sendMessage(`vuelo ${airlineCode}${flightNumber}`, { skipFlightDetection: false });
      }
    }
    await sendMessage(
      `origen ${origin.toUpperCase()} destino ${destination.toUpperCase()} fecha ${date} salida ${departure} llegada ${arrival}`,
      { skipFlightDetection: true },
    );
    setFlightModalOpen(false);
    setDetailPromptId(null);
    flightPromptMessageIdRef.current = null;
  };

  const closePassengerModal = () => {
    if (passengerModalData?.messageId) seatMessageIdRef.current = passengerModalData.messageId;
    setPassengerModalData(null);
  };

  const handleDetailPromptSend = () => {
    const text = detailPromptInput.trim();
    if (!text) return;
    sendMessage(text, { skipFlightDetection: true });
    setDetailPromptInput('');
  };

  const startSessionAndOpen = async () => {
    const activeSession = await ensureSession();
    if (activeSession) setIsOpen(true);
  };

  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  const showRegisterAction = pendingAction?.type === 'registerConfirm' && lastMessage && pendingAction.messageId === lastMessage.id;

  return (
    <div
      ref={widgetRef}
      className={`support-widget ${isOpen ? 'open' : ''}`}
      style={{ left: position.x, top: position.y }}
    >
      <div
        className="support-drag-handle"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        role="button"
        tabIndex={0}
        aria-label="Mover ventana de soporte"
      >
        <span className="support-handle-icon">⋮⋮</span>
      </div>

      {!isOpen && (
        <button
          type="button"
          className="support-launcher"
          onClick={startSessionAndOpen}
          aria-label="Abrir Tony Bot"
        >
          <span className="support-launcher-icon">🤖</span>
          <span className="support-launcher-label">Tony Bot</span>
          {unreadAlerts > 0 && (
            <span className="tony-alert-badge">{unreadAlerts}</span>
          )}
        </button>
      )}

      {isOpen && (
        <div className="chatbot-chat">
          <header
            className="chatbot-header"
            onMouseDown={startDrag}
            onTouchStart={startDrag}
          >
            <div className="chatbot-header-info">
              <h3>Tony Bot</h3>
              <p>Asistente de operaciones{estacion ? ` · ${estacion}` : ''}</p>
            </div>
            <div className="chatbot-header-controls">
              {/* TTS toggle */}
              <button
                type="button"
                className={`chatbot-tts-btn ${ttsEnabled ? 'active' : ''}`}
                onClick={() => {
                  if (ttsEnabled) window.speechSynthesis?.cancel();
                  setTtsEnabled((v) => !v);
                }}
                title={ttsEnabled ? 'Silenciar a Tony' : 'Activar voz de Tony'}
                aria-label="Toggle voz"
              >
                {ttsEnabled ? '🔊' : '🔇'}
              </button>
              <button
                type="button"
                className="chatbot-close"
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar chatbot"
              >
                ✕
              </button>
            </div>
          </header>

          <div className="chatbot-body" ref={chatBodyRef}>
            {loadingHistory && messages.length === 0 && (
              <div className="chatbot-loading">Cargando historial…</div>
            )}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot-message ${message.role === 'user' ? 'user' : 'assistant'} ${message.metadata?.isAlert ? `alert-${message.metadata.level}` : ''}`}
              >
                <div className="chatbot-message-author">
                  {message.role === 'user'
                    ? decoded?.nombreCompleto?.split(' ')[0] || 'Tú'
                    : 'Tony'}
                </div>
                <div className="chatbot-message-text">
                  {message.text.split('\n').map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="chatbot-message assistant typing">
                <div className="chatbot-message-author">Tony</div>
                <div className="chatbot-message-text typing-indicator">
                  <span /><span /><span />
                </div>
              </div>
            )}
            {showRegisterAction && (
              <div className="chatbot-actions">
                <span className="chatbot-actions-label">¿Quieres registrar este vuelo?</span>
                <div className="chatbot-actions-buttons">
                  <button type="button" onClick={() => handleQuickAction('Sí')}>Sí</button>
                  <button type="button" onClick={() => handleQuickAction('No')}>No</button>
                </div>
              </div>
            )}
            {detailPromptId && lastMessage && detailPromptId === lastMessage.id && !flightModalOpen && (
              <div className="chatbot-actions secondary">
                <span className="chatbot-actions-label">Capturar datos del vuelo</span>
                <div className="chatbot-actions-buttons">
                  <button type="button" onClick={() => openFlightForm()}>Abrir formulario</button>
                </div>
                <div className="chatbot-actions-input">
                  <input
                    type="text"
                    value={detailPromptInput}
                    onChange={(e) => setDetailPromptInput(e.target.value)}
                    placeholder="Ej. origen MEX destino GDL fecha 2025-03-12 salida 10:30 llegada 12:15"
                  />
                  <button type="button" onClick={handleDetailPromptSend} disabled={!detailPromptInput.trim()}>
                    Enviar
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Pregunta a Tony…"
              rows={2}
            />
            {/* Push-to-talk mic button */}
            {SpeechRecognition && (
              <button
                type="button"
                className={`chatbot-mic-btn ${isRecording ? 'recording' : ''}`}
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
                title="Mantén presionado para hablar"
                aria-label="Micrófono"
                disabled={isTyping}
              >
                🎤
              </button>
            )}
            <button type="button" onClick={handleSend} disabled={!inputValue.trim() || isTyping}>
              Enviar
            </button>
          </div>
        </div>
      )}

      {flightModalOpen && (
        <div className="chatbot-modal-overlay" onClick={closeFlightModal}>
          <div className="chatbot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chatbot-modal-header">
              <h4>Registrar nuevo vuelo</h4>
              <button type="button" onClick={closeFlightModal} aria-label="Cerrar formulario">✕</button>
            </div>
            <form className="chatbot-modal-body" onSubmit={handleFlightSubmit}>
              <div className="chatbot-form-grid">
                <label>Aerolínea<input type="text" value={flightForm.airline} maxLength={3} onChange={(e) => handleFlightChange('airline', e.target.value.toUpperCase())} placeholder="Ej. AM" /></label>
                <label>Vuelo<input type="text" value={flightForm.flightNumber} maxLength={5} onChange={(e) => handleFlightChange('flightNumber', e.target.value.toUpperCase())} placeholder="Ej. 0228" /></label>
                <label>Origen (IATA)<input type="text" value={flightForm.origin} maxLength={3} onChange={(e) => handleFlightChange('origin', e.target.value.toUpperCase())} placeholder="MEX" /></label>
                <label>Destino (IATA)<input type="text" value={flightForm.destination} maxLength={3} onChange={(e) => handleFlightChange('destination', e.target.value.toUpperCase())} placeholder="GDL" /></label>
                <label>Fecha<input type="date" value={flightForm.date} onChange={(e) => handleFlightChange('date', e.target.value)} /></label>
                <label>Salida (HH:MM)<input type="time" value={flightForm.departure} onChange={(e) => handleFlightChange('departure', e.target.value)} /></label>
                <label>Llegada (HH:MM)<input type="time" value={flightForm.arrival} onChange={(e) => handleFlightChange('arrival', e.target.value)} /></label>
              </div>
              <div className="chatbot-modal-actions">
                <button type="submit">Registrar vuelo</button>
                <button type="button" onClick={closeFlightModal} className="secondary">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {passengerModalData && (
        <div className="chatbot-modal-overlay" onClick={closePassengerModal}>
          <div className="chatbot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chatbot-modal-header">
              <h4>Servicios de silla · {passengerModalData.airline || ''}{passengerModalData.flightNumber}</h4>
              <button type="button" onClick={closePassengerModal} aria-label="Cerrar detalle">✕</button>
            </div>
            <div className="chatbot-modal-body">
              <div className="chatbot-flight-summary">
                <p><strong>Ruta:</strong> {passengerModalData.origin || '---'} → {passengerModalData.destination || '---'}</p>
                <p><strong>Fecha:</strong> {passengerModalData.date || '---'}</p>
                <p><strong>Servicios de silla:</strong> {passengerModalData.seatCount}</p>
              </div>
              <div className="chatbot-passenger-list">
                {passengerModalData.passengers && passengerModalData.passengers.length > 0 ? (
                  passengerModalData.passengers.map((pax, idx) => (
                    <div className="chatbot-passenger-card" key={pax.idPasajero || idx}>
                      <div className="chatbot-passenger-header">
                        <span className="chip">#{idx + 1}</span>
                        <span className="chip secondary">{pax.TipoSilla || 'WCHR'}</span>
                        {pax.TipoPasajero && <span className="chip secondary">Seat {pax.TipoPasajero}</span>}
                      </div>
                      <div className="chatbot-passenger-body">
                        <p className="name">{`${pax.Nombre || ''} ${pax.Apellido || ''}`.trim() || 'Pasajero'}</p>
                        <p className="meta"><strong>PNR:</strong> {pax.pnr || '—'}</p>
                        <p className="meta"><strong>Abordo:</strong> {pax.Abordo ? 'Sí' : 'No'}</p>
                        <p className="meta"><strong>Origen servicio:</strong> {pax.OrigenServicio || '—'}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="chatbot-passenger-empty">No se recibieron pasajeros detallados.</div>
                )}
              </div>
              <div className="chatbot-modal-actions">
                <button type="button" onClick={closePassengerModal} className="primary">Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportWidget;
